/**
 * Canonical composers management view.
 *
 * Shows all canonical composers (not backed by Wikidata) with piece counts.
 * Allows admins to create, edit, delete, and promote composers to Wikidata.
 */

import { useState } from 'react'
import { useCanonicalComposers } from '../hooks/useCanonicalComposers'
import { Card, CardDescription, CardHeader, CardTitle } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { ManagementTabs } from '../components/shared/ManagementTabs'
import { Loader2, Plus } from 'lucide-react'
import { CanonicalComposersTable } from '../components/canonical-composers/CanonicalComposersTable'
import { CanonicalComposerDetailsDialog } from '../components/canonical-composers/CanonicalComposerDetailsDialog'
import { EditCanonicalComposerDialog } from '../components/canonical-composers/EditCanonicalComposerDialog'
import type { CanonicalComposer } from '../types/canonical-composer'

export function CanonicalComposers() {
  const { data: canonicalComposers, isLoading, error } = useCanonicalComposers()
  const [selectedComposer, setSelectedComposer] = useState<CanonicalComposer | null>(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  const handleViewDetails = (composer: CanonicalComposer) => {
    setSelectedComposer(composer)
    setDetailsDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load canonical composers: {error.message}</AlertDescription>
      </Alert>
    )
  }

  if (!canonicalComposers || canonicalComposers.length === 0) {
    return (
      <div className="space-y-6">
        <ManagementTabs />

        <Card>
          <CardHeader>
            <CardTitle>Canonical Composers</CardTitle>
            <CardDescription>
              No canonical composers yet. Canonical composers are created for composers not found in
              Wikidata.
            </CardDescription>
          </CardHeader>
        </Card>

        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Composer
        </Button>

        <EditCanonicalComposerDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ManagementTabs />

      <div className="flex items-center justify-between">
        <p className="text-muted-foreground">
          Manage canonical composers not backed by Wikidata references. Each composer shows how many
          canonical pieces are linked to them.
        </p>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create New Composer
        </Button>
      </div>

      <CanonicalComposersTable composers={canonicalComposers} onViewDetails={handleViewDetails} />

      {selectedComposer && (
        <CanonicalComposerDetailsDialog
          composer={selectedComposer}
          open={detailsDialogOpen}
          onOpenChange={setDetailsDialogOpen}
        />
      )}

      <EditCanonicalComposerDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
    </div>
  )
}
